import React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { push } from "store/actions/router";
import classnames from "classnames";
import { get, includes, isEmpty, minBy } from "lodash";
import moment from "moment";
import { formatCurrency, formatPercentage } from "utils/number";
import "./AnnualContributionsWidget.scss";
import Button from "components/Button";
import ContributionModal from "pages/dashboards/individualDashboard/dashboard/ContributionModal";
import { contributionAbilitySelector } from "store/selectors/contribution";
import { investmentAccountsSelector } from "store/selectors/accounts";
import { bankSelector } from "store/selectors/bank";
import {
  Card,
  Col,
  Container,
  NavDropdown,
  Row,
  Tab,
  Tabs,
} from "react-bootstrap";
import { getIraAccountIdSelector } from "store/selectors/user";
import {
  FiActivity,
  FiAward,
  FiBell,
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiTarget,
} from "react-icons/fi";
import type { RootState } from "types/state";

// Minimal shape of the per-year contribution entries the widget reads from
// `contributionAbility.history` (mirrors the schema's ContributionAbilityByYear).
interface ContributionYear {
  taxYear: number;
  totalContributionAmount: number;
  limit: number;
  amountRemaining: number;
}

function bankIsVerified(bankState: string | null | undefined) {
  const activeBankStates = ["BankAccountPendingAchLink", "BankAccountActive"];
  return includes(activeBankStates, bankState);
}

const mapStateToProps = (state: RootState) => {
  const accountId = getIraAccountIdSelector(state);

  return {
    accountId,
    contributionAbility: contributionAbilitySelector(state),
    contributionEstimates: state.contribution.contributionEstimates,
    bank: bankSelector(state),
    iraAccounts: investmentAccountsSelector(state),
  };
};

const mapDispatchToProps = {
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  priorYearContributionAbility?: object;
  currentContributionAbility?: object;
  getContributionAbilityAndEstimates?: (...args: unknown[]) => void;
  estimatesType?: object;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  showContributionModal: boolean;
  selectedHistoryYear: number | null;
}

class AnnualContributionsWidget extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      showContributionModal: false,
      selectedHistoryYear: null,
    };
  }

  componentDidMount() {
    window.analytics.page("Annual Contributions Widget");
  }

  _onContinueClick = () => {
    this.setState({
      showContributionModal: true,
    });
  };

  _getSelectableHistoryYears = (): ContributionYear[] => {
    const history = (get(this.props, "contributionAbility.history", []) ||
      []) as ContributionYear[];
    const currentTaxYear = get(
      this.props,
      "contributionAbility.currentYear.taxYear"
    );
    const iraAccounts = this.props.iraAccounts;

    if (isEmpty(iraAccounts) || isEmpty(history)) {
      return [];
    }

    const earliestAccount = minBy(iraAccounts, (account) =>
      moment(account.createdAt).valueOf()
    );
    // PRESERVED BUG (conversion-only): minBy can return undefined; original
    // dereferenced .createdAt unguarded, throwing if iraAccounts were empty
    // (unreachable here due to the isEmpty early return above).
    const earliestAccountYear = moment(earliestAccount!.createdAt).year();
    const isWithinWindow = get(
      this.props,
      "contributionAbility.isWithinPriorYearContributionWindow",
      false
    );

    let earliestYear = earliestAccountYear;
    if (earliestAccountYear === currentTaxYear && isWithinWindow) {
      earliestYear = earliestAccountYear - 1;
    }

    return history.filter(
      (entry) =>
        entry.taxYear !== currentTaxYear && entry.taxYear >= earliestYear
    );
  };

  _shouldShowPriorYearsTab = () => {
    return this._getSelectableHistoryYears().length > 0;
  };

  _getSelectedHistoryYearData = () => {
    const selectable = this._getSelectableHistoryYears();
    if (selectable.length === 0) return null;

    const isWithinWindow = get(
      this.props,
      "contributionAbility.isWithinPriorYearContributionWindow",
      false
    );
    const priorTaxYear = get(
      this.props,
      "contributionAbility.priorYear.taxYear"
    ) as number | undefined;

    const defaultYear =
      isWithinWindow && priorTaxYear ? priorTaxYear : selectable[0].taxYear;
    const yearToShow =
      this.state.selectedHistoryYear == null
        ? defaultYear
        : this.state.selectedHistoryYear;
    return (
      selectable.find((entry) => entry.taxYear === yearToShow) || selectable[0]
    );
  };

  _canContributeToYear = (taxYear: number) => {
    const isWithinWindow = get(
      this.props,
      "contributionAbility.isWithinPriorYearContributionWindow",
      false
    );
    const priorTaxYear = get(
      this.props,
      "contributionAbility.priorYear.taxYear"
    ) as number | undefined;
    return isWithinWindow && taxYear === priorTaxYear;
  };

  render() {
    // These `get` lookups can yield undefined; the original used the values as
    // bare numbers (unguarded arithmetic / formatting). `as unknown as number`
    // preserves that unguarded numeric use without changing behavior.
    const totalContributionAmount = get(
      this.props,
      "contributionAbility.currentYear.totalContributionAmount"
    ) as unknown as number;

    const currentYearLimit = get(
      this.props,
      "contributionAbility.currentYear.limit"
    ) as unknown as number;

    const amountRemaining = get(
      this.props,
      "contributionAbility.currentYear.amountRemaining"
    ) as unknown as number;
    const currentTaxYear = get(
      this.props,
      "contributionAbility.currentYear.taxYear"
    ) as unknown as number;

    const showPriorYearsTab = this._shouldShowPriorYearsTab();
    const selectableHistoryYears = this._getSelectableHistoryYears();
    const selectedHistoryData = showPriorYearsTab
      ? this._getSelectedHistoryYearData()
      : null;
    const selectedYearCanContribute =
      selectedHistoryData &&
      this._canContributeToYear(selectedHistoryData.taxYear);
    const selectedYearMaxedOut =
      selectedHistoryData &&
      selectedHistoryData.totalContributionAmount >= selectedHistoryData.limit;
    const showBell = selectedYearCanContribute && !selectedYearMaxedOut;
    const priorTabLabel =
      selectableHistoryYears.length > 1
        ? "Prior Years"
        : selectedHistoryData && selectedHistoryData.taxYear;

    const monthlyTotal = get(
      this.props,
      "contributionEstimates.currentMonthAmount"
    ) as unknown as number;

    const yearlyEstimatedTotal = get(
      this.props,
      "contributionEstimates.currentYearAmount"
    ) as unknown as number;

    const showOneTimeContributionButton =
      this.props.bank && bankIsVerified(this.props.bank.state);

    return (
      <div className="annual-contribution-widget">
        <h4 className="annual-contribution-title">
          Annual Contribution Progress
        </h4>
        <Tabs defaultActiveKey={currentTaxYear} id="uncontrolled-tab-example">
          <Tab eventKey={currentTaxYear} title={currentTaxYear}>
            <Card>
              <Card.Title className="left">Summary of your progress</Card.Title>
              <Card.Body>
                <Container>
                  <Row>
                    <Col>
                      <div className="total-contributed-title">
                        <div className="total-contributed">
                          <p className="total">Total contributed</p>
                          <p className="amount">
                            {formatCurrency(totalContributionAmount)}
                          </p>
                        </div>
                        <div className="contribution-bar">
                          <div
                            className="contribution-fill-current-year"
                            style={{
                              width: formatPercentage(
                                totalContributionAmount / currentYearLimit
                              ),
                            }}
                          ></div>
                        </div>
                        <p className="annual-maximum">Max</p>
                      </div>
                    </Col>
                    <Col>
                      <div className="contribution">
                        <FiCalendar
                          size={"18px"}
                          color={"#1D99A9"}
                          strokeWidth={"2"}
                        />
                        <div className="contribution-title">
                          <p className="contribution-type">Monthly recurring</p>
                          <p className="contribution-type-subtext">
                            Your contribution amount for this month is{" "}
                            {formatCurrency(monthlyTotal)}
                          </p>
                        </div>
                      </div>
                      <div className="current-year-contribution">
                        <FiActivity
                          size={"18px"}
                          color={"#1D99A9"}
                          strokeWidth={"2"}
                        />
                        <div className="contribution-title">
                          <p className="contribution-type">
                            Total amount contributed
                          </p>
                          <p className="contribution-type-subtext">
                            You have reached{" "}
                            {formatPercentage(
                              totalContributionAmount / currentYearLimit,
                              0
                            )}{" "}
                            of your annual maximum. This means you have{" "}
                            {formatCurrency(amountRemaining)} left to contribute
                            to your IRA.
                          </p>
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="total-contributed-title">
                        <div className="total-contributed">
                          <p className="total">Estimated annual contribution</p>
                          <p className="amount">
                            {formatCurrency(yearlyEstimatedTotal)}
                          </p>
                        </div>
                        <div className="contribution-bar">
                          <div
                            className="contribution-fill"
                            style={{
                              width: formatPercentage(
                                yearlyEstimatedTotal / currentYearLimit
                              ),
                            }}
                          ></div>
                        </div>
                        <p className="annual-maximum">Max</p>
                      </div>
                    </Col>
                    <Col>
                      <div className="current-year-contribution">
                        <FiTarget
                          size={"18px"}
                          color={"#1D99A9"}
                          strokeWidth={"2"}
                        />
                        <div className="contribution-title">
                          <p className="contribution-type">
                            Estimated annual contribution
                          </p>
                          <p className="contribution-type-subtext">
                            Based on your monthly contributions you will reach{" "}
                            {formatPercentage(
                              yearlyEstimatedTotal / currentYearLimit,
                              0
                            )}{" "}
                            of your annual IRA maximum.
                          </p>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Container>
              </Card.Body>
            </Card>
          </Tab>
          {showPriorYearsTab && (
            <Tab
              eventKey="profile"
              title={
                <span className="tax-year-tab">
                  <span className="prior-year-tab"> {priorTabLabel}</span>
                  {showBell && (
                    <span className="bell">
                      <FiBell
                        className="notification"
                        size={"14px"}
                        color={"#FFFFFF"}
                        fill={"#FFFFFF"}
                        stroke={"#6772E5"}
                        strokeWidth={"2"}
                      />{" "}
                    </span>
                  )}
                </span>
              }
            >
              <Card>
                <Card.Body>
                  {selectableHistoryYears.length > 1 && (
                    <div className="prior-year-selector">
                      <NavDropdown
                        id="prior-year-dropdown"
                        title={
                          <div className="prior-year-selector-target-box">
                            <p className="active-year-name">
                              Tax year {selectedHistoryData!.taxYear}
                              <span className="down-arrow">
                                <FiChevronDown
                                  size={14}
                                  className="nav-chevron"
                                />
                              </span>
                            </p>
                          </div>
                        }
                      >
                        <div className="action-list-popover prior-year-dropdown">
                          <div className="actions-list">
                            {selectableHistoryYears.map((entry) => (
                              <NavDropdown.Item
                                key={entry.taxYear}
                                className={classnames("action-item", {
                                  ["is-selected"]:
                                    selectedHistoryData!.taxYear ===
                                    entry.taxYear,
                                })}
                                onClick={() =>
                                  this.setState({
                                    selectedHistoryYear: entry.taxYear,
                                  })
                                }
                              >
                                {entry.taxYear}{" "}
                                {selectedHistoryData!.taxYear ===
                                  entry.taxYear && (
                                  <FiCheck stroke={"#0A2540"} size={14} />
                                )}
                              </NavDropdown.Item>
                            ))}
                          </div>
                        </div>
                      </NavDropdown>
                    </div>
                  )}
                  {selectedYearCanContribute && !selectedYearMaxedOut && (
                    <Row>
                      <Col md={6}>
                        <Card.Title className="prior-year-card-title">
                          Summary of {selectedHistoryData!.taxYear}{" "}
                          progress{" "}
                        </Card.Title>
                        <p className="title-subtext">
                          You haven{"'"}t reached your annual maximum just yet.
                          You are still able to contribute to your{" "}
                          {selectedHistoryData!.taxYear} IRA! You have{" "}
                          {formatCurrency(selectedHistoryData!.amountRemaining)}{" "}
                          left to max out your annual maximum.
                        </p>
                        <ContributionModal
                          show={this.state.showContributionModal}
                          options={{ isApplyToPriorYear: true }}
                          onSuccess={() => {
                            this.setState({
                              showContributionModal: false,
                            });
                          }}
                          onClose={() => {
                            this.setState({
                              showContributionModal: false,
                            });
                          }}
                        />
                      </Col>
                      <Col md={6}>
                        <div className="contribution">
                          <FiActivity
                            size={"18px"}
                            color={"#FFFFFF"}
                            fill={"#FFFFFF"}
                            stroke={"#6772E5"}
                            strokeWidth={"2"}
                          />
                          <div className="contribution-title">
                            <p className="contribution-type">
                              Total amount contributed
                            </p>
                            <p className="contribution-type-subtext">
                              You have reached{" "}
                              {formatPercentage(
                                selectedHistoryData!.totalContributionAmount /
                                  selectedHistoryData!.limit,
                                0
                              )}{" "}
                              of your annual maximum.
                            </p>
                          </div>
                        </div>
                        <div className="prior-year-total-contributed-title ">
                          <div className="total-contributed">
                            <p className="total">Total contributed</p>
                            <p className="amount">
                              {formatCurrency(
                                selectedHistoryData!.totalContributionAmount
                              )}
                            </p>
                          </div>
                          <div className="contribution-bar">
                            <div
                              className="contribution-fill-current-year"
                              style={{
                                width: formatPercentage(
                                  selectedHistoryData!.totalContributionAmount /
                                    selectedHistoryData!.limit
                                ),
                              }}
                            ></div>
                          </div>
                          <p className="annual-maximum">Max</p>
                        </div>
                        {!showOneTimeContributionButton && (
                          <p className="title-subtext">
                            Link your bank account to start making
                            contributions.
                          </p>
                        )}
                        {showOneTimeContributionButton && (
                          <Button
                            className="contribute-btn-2020"
                            btnLabel="Contribute"
                            name="action"
                            onClick={this._onContinueClick}
                            withArrow={true}
                          />
                        )}
                      </Col>
                    </Row>
                  )}
                  {(!selectedYearCanContribute || selectedYearMaxedOut) && (
                    <Row>
                      <Col md={6}>
                        <Card.Title className="prior-year-card-title">
                          {selectedYearCanContribute && selectedYearMaxedOut ? (
                            <>
                              You’ve reached your IRA contribution Limit!{" "}
                              <FiAward
                                size={"18px"}
                                fill={"#FFFFFF"}
                                color={"#FFFFFF"}
                                stroke={"#6772E5"}
                                strokeWidth={"2"}
                              />
                            </>
                          ) : (
                            <>
                              Summary of {selectedHistoryData!.taxYear} progress
                            </>
                          )}
                        </Card.Title>
                        <p className="title-subtext">
                          {selectedYearCanContribute && selectedYearMaxedOut
                            ? `Congratulations! Thanks to your automatic deposit plan you’ve reached your ${formatCurrency(
                                selectedHistoryData!.limit,
                                0
                              )} IRA contribution limit for the year. In January we will restart your IRA automatic deposits so you can max out next year’s contributions.`
                            : `The ${
                                selectedHistoryData!.taxYear
                              } contribution window has closed. You contributed ${formatCurrency(
                                selectedHistoryData!.totalContributionAmount
                              )} of your ${formatCurrency(
                                selectedHistoryData!.limit,
                                0
                              )} annual maximum.`}
                        </p>
                      </Col>
                      <Col md={6}>
                        <div className="contribution">
                          <FiActivity
                            size={"18px"}
                            color={"#FFFFFF"}
                            fill={"#FFFFFF"}
                            stroke={"#6772E5"}
                            strokeWidth={"2"}
                          />
                          <div className="contribution-title">
                            <p className="contribution-type">
                              Total amount contributed
                            </p>
                            <p className="contribution-type-subtext">
                              You reached{" "}
                              {formatPercentage(
                                selectedHistoryData!.totalContributionAmount /
                                  selectedHistoryData!.limit,
                                0
                              )}{" "}
                              of the annual maximum.
                            </p>
                          </div>
                        </div>
                        <div className="maxed-total-contributed-title">
                          <div className="total-contributed">
                            <p className="total">Total contributed</p>
                            <p className="amount">
                              {formatCurrency(
                                selectedHistoryData!.totalContributionAmount
                              )}
                            </p>
                          </div>
                          <div className="contribution-bar">
                            <div
                              className="contribution-fill"
                              style={{
                                width: formatPercentage(
                                  selectedHistoryData!.totalContributionAmount /
                                    selectedHistoryData!.limit
                                ),
                              }}
                            ></div>
                          </div>
                          <p className="annual-maximum">Max</p>
                        </div>
                      </Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>
            </Tab>
          )}
        </Tabs>
      </div>
    );
  }
}

export default connector(withApollo<Props>(AnnualContributionsWidget));
